import {BaseQueryFn} from '@reduxjs/toolkit/dist/query';
import {traceBaseQueryFn} from './traceBaseQuery';
import {actionRequiresAuth} from '@src/Services/AccessControl/accessRules';
import {AxiosRequestConfig} from 'axios';
import getFirebaseTokenId from './utils/getFirebaseToken';

interface AuthGuardBaseQueryArgs {
  url: string;
  baseUrl?: string;
  method?: AxiosRequestConfig['method'];
  body?: any;
  params?: any;
  apiVersion?: string;
  contentType?: string;
  noAuth?: boolean;
  noSession?: boolean;
  headers?: Record<string, string | undefined>;
  // Nouvelles options pour l'AuthGuard
  actionId?: string; // ID de l'action pour vérifier les règles d'accès
  requiresAuth?: boolean; // Forcer la vérification d'auth
  skipAuthGuard?: boolean; // Ignorer l'AuthGuard pour cet appel
}

/**
 * Helper pour vérifier si l'utilisateur est connecté
 * Utilise getFirebaseTokenId pour obtenir le token Firebase
 */
const isUserAuthenticated = async (): Promise<boolean> => {
  try {
    const token = await getFirebaseTokenId();
    return !!token && token.length > 0;
  } catch (error) {
    console.log('🔍 [AuthGuard] Error checking authentication:', error);
    return false;
  }
};

/**
 * BaseQuery wrapper qui intègre le système AuthGuard
 * Vérifie l'authentification avant d'exécuter les appels d'API
 */
export const authGuardBaseQuery: BaseQueryFn<
  AuthGuardBaseQueryArgs,
  unknown,
  unknown,
  {}
> = async (args, api, extraOptions) => {
  const {
    actionId,
    requiresAuth = false,
    skipAuthGuard = false,
    ...baseQueryArgs
  } = args;

  // Ignorer l'AuthGuard si explicitement demandé
  if (skipAuthGuard) {
    return traceBaseQueryFn(baseQueryArgs, api, extraOptions);
  }

  // Vérifier l'authentification selon les règles définies
  let needsAuth = requiresAuth;

  // Vérifier les règles d'accès si un actionId est fourni
  if (actionId && !needsAuth) {
    const accessRule = actionRequiresAuth(actionId);
    needsAuth = !!accessRule;
  }

  // Vérifier si la méthode nécessite une authentification par défaut
  const writeOperations = ['POST', 'PUT', 'DELETE', 'PATCH'];
  if (
    !needsAuth &&
    args.method &&
    writeOperations.includes(args.method.toUpperCase())
  ) {
    // Les opérations d'écriture nécessitent généralement une authentification
    needsAuth = true;
  }

  // Si l'authentification est requise, vérifier l'état de l'utilisateur
  if (needsAuth && !(await isUserAuthenticated())) {
    console.log(
      `🔒 [AuthGuardBaseQuery] Auth required for ${args.method} ${args.url}`,
    );

    // Déclencher l'auth modal via un événement personnalisé
    // Cela sera capté par le GlobalAuthGuardProvider
    const authEvent = new CustomEvent('authRequired', {
      detail: {
        actionId,
        endpoint: args.url,
        method: args.method,
        reason: `API call requires authentication: ${actionId || 'unknown action'}`,
      },
    });

    if (typeof window !== 'undefined') {
      window.dispatchEvent(authEvent);
    }

    // Retourner une erreur d'autorisation
    return {
      error: {
        status: 401,
        data: {
          message: 'Authentication required',
          actionId,
          endpoint: args.url,
          requiresModal: true,
        },
      },
    };
  }

  // Exécuter l'appel d'API normal
  return traceBaseQueryFn(baseQueryArgs, api, extraOptions);
};

/**
 * Helper pour créer des endpoints avec AuthGuard intégré
 */
export const createSecureEndpoint = (
  baseArgs: Omit<AuthGuardBaseQueryArgs, 'actionId' | 'requiresAuth'>,
  authConfig: {
    actionId?: string;
    requiresAuth?: boolean;
  } = {},
): AuthGuardBaseQueryArgs => ({
  ...baseArgs,
  ...authConfig,
});

/**
 * Configurations prédéfinies pour les actions communes
 */
export const AUTH_CONFIGS = {
  // Actions de favoris/bookmarks
  BOOKMARK_ADD: {actionId: 'bookmark-add', requiresAuth: true},
  BOOKMARK_REMOVE: {actionId: 'bookmark-remove', requiresAuth: true},

  // Actions de like
  LIKE_CONTENT: {actionId: 'like-content', requiresAuth: true},

  // Actions de commentaires
  COMMENT_ADD: {actionId: 'comment-add', requiresAuth: true},

  // Actions de playlist
  PLAYLIST_CREATE: {actionId: 'playlist-create', requiresAuth: true},
  PLAYLIST_ADD_CONTENT: {actionId: 'playlist-add-content', requiresAuth: true},

  // 🎓 Actions liées aux cours (COURSE_SINGLE sécurisé)
  COURSE_ACCESS: {actionId: 'course-access', requiresAuth: true},
  COURSE_PROGRESS: {actionId: 'course-progress', requiresAuth: true},
  COURSE_COMPLETION: {actionId: 'course-completion', requiresAuth: true},
  EXAM_SUBMISSION: {actionId: 'exam-submission', requiresAuth: true},
  UNIT_PROGRESSION: {actionId: 'unit-progression', requiresAuth: true},
  COURSE_STATUS_UPDATE: {actionId: 'course-status-update', requiresAuth: true},

  // 🎵 Actions liées aux players audio/vidéo (sécurisés)
  PLAYER_ACCESS: {actionId: 'player-access', requiresAuth: true},
  AUDIO_PLAY: {actionId: 'audio-play', requiresAuth: true},
  VIDEO_PLAY: {actionId: 'video-play', requiresAuth: true},
  PLAYER_PROGRESS: {actionId: 'player-progress', requiresAuth: true},
  PLAYER_QUALITY_CHANGE: {
    actionId: 'player-quality-change',
    requiresAuth: true,
  },
  PLAYER_SPEED_CHANGE: {actionId: 'player-speed-change', requiresAuth: true},

  // Actions premium
  DOWNLOAD_CONTENT: {actionId: 'download-content', requiresAuth: true},
  PREMIUM_CONTENT_ACCESS: {
    actionId: 'premium-content-access',
    requiresAuth: true,
  },

  // Pas d'auth requise
  PUBLIC_CONTENT: {requiresAuth: false, skipAuthGuard: true},
} as const;
